import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import useMagneticEffect from '../hooks/useMagneticEffect';

const Blog = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const buttonRef = useMagneticEffect(0.4);

  const posts = [
    {
      category: 'Web & Digital Design',
      title: 'Building a Thriving Partnership with Your Digital Agency',
      image: 'https://images.unsplash.com/photo-1570841512628-a93d7bc5aaf4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHx0ZWNobm9sb2d5JTIwYmxvZ3xlbnwwfHx8fDE3NjkxODY2MjV8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      category: 'Web & Digital Design',
      title: 'The Modern Web Playbook: Launching a SaaS Site That Resonates, Delivers, & Sets Your Brand Apart',
      image: 'https://images.unsplash.com/photo-1607125517027-51e6eb339ea4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHw0fHx0ZWNobm9sb2d5JTIwYmxvZ3xlbnwwfHx8fDE3NjkxODY2MjV8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      category: 'SEO & Digital Marketing',
      title: 'Where AI Meets Growth: Future-Ready Marketing for B2B Companies',
      image: 'https://images.pexels.com/photos/57690/pexels-photo-57690.jpeg'
    },
    {
      category: 'Web & Digital Design',
      title: 'Designing High-Intent Journeys: Conversion Rate Optimization for Modern B2B Brands',
      image: 'https://images.pexels.com/photos/12679990/pexels-photo-12679990.jpeg'
    }
  ];

  return (
    <section id="blog" ref={ref} className="bg-[#1a1a1a] py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 md:px-12 relative overflow-hidden">
      {/* Subtle animated grid pattern - CSS only */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none animate-grid"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12 sm:mb-14 md:mb-16"
        >
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-wider mb-3 sm:mb-4"
          >
            Latest Insights
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-gray-400 text-base sm:text-lg font-light"
          >
            Our thoughts and perspectives on digital.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-12 mb-12 sm:mb-14 md:mb-16">
          {posts.map((post, index) => (
            <motion.article
              key={index}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{
                duration: 0.6,
                delay: 0.2 + index * 0.1,
                ease: [0.6, 0.05, 0.01, 0.9]
              }}
              onHoverStart={() => setHoveredIndex(index)}
              onHoverEnd={() => setHoveredIndex(null)}
              className="group cursor-pointer will-change-transform"
              data-cursor="explore"
            >
              <div className="relative overflow-hidden rounded-lg mb-4 sm:mb-5 md:mb-6 aspect-video">
                <motion.img
                  src={post.image}
                  alt={post.title}
                  loading="lazy"
                  className="w-full h-full object-cover will-change-transform"
                  animate={{
                    scale: hoveredIndex === index ? 1.08 : 1
                  }}
                  transition={{ duration: 0.6, ease: [0.6, 0.05, 0.01, 0.9] }}
                />
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: hoveredIndex === index ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"
                />
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{
                    opacity: hoveredIndex === index ? 1 : 0,
                    y: hoveredIndex === index ? 0 : 20
                  }}
                  transition={{ duration: 0.3 }}
                  className="absolute bottom-4 sm:bottom-6 right-4 sm:right-6"
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-orange-500 flex items-center justify-center">
                    <ArrowRight size={16} className="text-white sm:w-[18px] sm:h-[18px]" />
                  </div>
                </motion.div>
              </div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                className="text-orange-500 text-[10px] sm:text-xs tracking-wider mb-2 sm:mb-3 font-medium uppercase"
              >
                {post.category}
              </motion.p>
              <motion.h3
                animate={{ color: hoveredIndex === index ? '#ff5722' : '#ffffff' }}
                transition={{ duration: 0.3 }}
                className="text-lg sm:text-xl md:text-2xl font-semibold leading-tight"
              >
                {post.title}
              </motion.h3>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center"
        >
          <motion.button
            ref={buttonRef}
            whileHover={{ x: 8 }}
            className="group inline-flex items-center gap-2 sm:gap-3 text-white hover:text-orange-500 transition-colors duration-300 text-base sm:text-lg font-light tracking-wide will-change-transform"
          >
            View more insights
            <motion.div
              animate={{ x: [0, 4, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <ArrowRight size={18} className="sm:w-5 sm:h-5" />
            </motion.div>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Blog;